import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  chatThemeSchema,
  resolveLanguage,
  VISITOR_NAME_AAD_CID,
  type ChatTheme,
  type InvitationPreview,
} from '@kairelo/shared';
import { visitor, setVisitorToken, ApiRequestError } from '../lib/api.js';
import {
  clearFragment,
  encryptPayload,
  keyFromFragmentValue,
  readKeyFromFragment,
} from '../lib/crypto.js';
import { checkKeyStorage, storeConversationKey } from '../lib/keystore.js';
import { saveVisitorSession } from '../lib/visitorSession.js';
import { ThemedRegion } from '../components/ThemeStyle.tsx';
import { I18nProvider, useT } from '../i18n/I18n.tsx';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Disclosure, Field, Loading, Notice } from '../components/ui.tsx';
import {
  EndpointCopyingNotice,
  NotAnonymousNotice,
  TemporaryNotice,
} from '../components/Disclosures.tsx';

/**
 * The visitor's landing page (concept §4).
 *
 * Rendering this performs an ordinary GET, which activates nothing and claims
 * nothing — a link preview or an email scanner leaves the invitation exactly as
 * it found it.
 *
 * The conversation key is read from the URL fragment **locally**, never put in a
 * request, and cleared from the address bar as soon as it has been read.
 *
 * Layout note: this is the screen a stranger meets first, so the owner's profile
 * and a single clear action lead. The required disclosures are all still here —
 * the two that change what someone is agreeing to sit above the fold as a
 * compact strip, and the full text is one click away rather than three stacked
 * banners pushing the join form off the screen.
 */
export function Join() {
  return (
    <I18nProvider language={resolveLanguage(navigator.language)}>
      <JoinInner />
    </I18nProvider>
  );
}

function JoinInner() {
  const t = useT();
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyValue] = useState<string | null>(() => readKeyFromFragment(location.hash));

  useEffect(() => {
    if (keyValue) clearFragment();
  }, [keyValue]);

  useEffect(() => {
    visitor
      .preview(token)
      .then(setPreview)
      .catch(() => setPreview({ state: 'unknown' }));
  }, [token]);

  const theme: ChatTheme = useMemo(
    () => preview?.profile?.theme ?? chatThemeSchema.parse({}),
    [preview],
  );

  if (preview === null) {
    return (
      <div className="page page--narrow">
        <Loading>{t('join.opening')}</Loading>
      </div>
    );
  }

  if (preview.state !== 'joinable') {
    const message =
      preview.state === 'claimed'
        ? t('join.claimed')
        : preview.state === 'expired'
          ? t('join.expired', { hours: 24 })
          : t('join.invalid');
    return (
      <div className="page page--narrow">
        <div className="empty">
          <span className="empty__icon">
            <Icon name="clock" size={22} />
          </span>
          <h3>{t('join.unusableTitle')}</h3>
          <p>{message}</p>
          <p className="text-sm muted">{t('join.askForNew')}</p>
        </div>
      </div>
    );
  }

  if (!keyValue) {
    return (
      <div className="page page--narrow">
        <div className="empty">
          <span className="empty__icon">
            <Icon name="key" size={22} />
          </span>
          <h3>{t('join.incompleteTitle')}</h3>
          <p>
            The part after the <code>#</code> holds the key that decrypts this conversation. It
            never reaches the server, so it cannot be looked up or resent.
          </p>
          <p className="text-sm muted">{t('join.incompleteHint')}</p>
        </div>
      </div>
    );
  }

  const hours = Math.round((preview.activeTtlSeconds ?? 86400) / 3600);

  const submit = async () => {
    setError(null);
    setJoining(true);
    try {
      const storage = await checkKeyStorage();
      const key = await keyFromFragmentValue(keyValue);

      // The display name is conversation content, so it is encrypted here,
      // before it is sent (concept §6). The conversation id does not exist yet,
      // so it binds to the documented AAD constant.
      const encryptedName = await encryptPayload({
        key,
        conversationId: VISITOR_NAME_AAD_CID,
        sender: 'visitor',
        payload: { kind: 'visitor_name', displayName: displayName.trim() },
      });

      const result = await visitor.join(token, encryptedName);
      setVisitorToken(result.sessionToken);
      saveVisitorSession({
        conversationId: result.conversationId,
        sessionToken: result.sessionToken,
        expiresAt: result.expiresAt,
      });

      if (storage.available) {
        await storeConversationKey({
          conversationId: result.conversationId,
          key,
          role: 'visitor',
          expiresAt: new Date(result.expiresAt).getTime(),
        });
      }

      navigate(`/chat/${result.conversationId}`, { replace: true });
    } catch (joinError) {
      setJoining(false);
      setError(joinError instanceof ApiRequestError ? joinError.message : t('join.failed'));
    }
  };

  const profile = preview.profile;
  // The first photo becomes the portrait; the rest stay as a strip below.
  const portrait = profile?.media.find((item) => item.kind === 'photo');
  const rest = profile?.media.filter((item) => item !== portrait) ?? [];

  return (
    <ThemedRegion theme={theme}>
      <div className="page page--narrow join">
        {/*
          A profile page, not a form with a name above it. This is a stranger's
          first contact with the owner, and deciding whether to join is mostly a
          question of who is asking.
        */}
        <header className="join__hero">
          {portrait ? (
            <img className="join__portrait" src={portrait.url} alt="" />
          ) : (
            <span className="join__portrait join__portrait--fallback" aria-hidden="true">
              {profile?.displayName?.[0]?.toUpperCase() ?? <Icon name="user" size={40} />}
            </span>
          )}

          <p className="join__invited">{t('join.invitedBy')}</p>
          <h1>{profile?.displayName}</h1>
          {profile?.headline ? <p className="join__headline">{profile.headline}</p> : null}
          <p className="join__bio">
            {profile?.bio?.trim()
              ? profile.bio
              : t('join.defaultBio', { name: profile?.displayName ?? '' })}
          </p>
        </header>

        {rest.length > 0 ? (
          <div className="media-strip join__media">
            {rest.map((item) =>
              item.kind === 'photo' ? (
                <img key={item.objectKey} src={item.url} alt="" />
              ) : (
                <video key={item.objectKey} src={item.url} controls playsInline />
              ),
            )}
          </div>
        ) : null}

        {/* The two facts that change what someone is agreeing to, kept in view. */}
        <div className="trust-strip" style={{ marginBottom: 'var(--s-4)' }}>
          <span>
            <Icon name="lock" size={14} />
            {t('join.trustEncrypted')}
          </span>
          <span>
            <Icon name="clock" size={14} />
            {t('join.trustEnds', { hours })}
          </span>
          <span>
            <Icon name="user" size={14} />
            {t('join.trustNoAccount')}
          </span>
        </div>

        <Card>
          <h2 style={{ marginBottom: 'var(--s-2)' }}>{t('join.title')}</h2>
          <p className="text-sm muted" style={{ marginBottom: 'var(--s-4)' }}>
            {t('join.subtitle')}
          </p>

          <Field label={t('join.nameLabel')} htmlFor="display-name" hint={t('join.nameHint')}>
            {({ id, describedBy }) => (
              <input
                id={id}
                type="text"
                value={displayName}
                maxLength={80}
                autoFocus
                aria-describedby={describedBy}
                onChange={(event) => setDisplayName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && displayName.trim().length > 0) void submit();
                }}
                placeholder={t('join.namePlaceholder')}
              />
            )}
          </Field>

          {error ? (
            <div style={{ marginTop: 'var(--s-4)' }}>
              <Notice tone="danger">{error}</Notice>
            </div>
          ) : null}

          <div style={{ marginTop: 'var(--s-5)' }}>
            <Button
              variant="primary"
              size="lg"
              icon="message"
              block
              busy={joining}
              disabled={displayName.trim().length === 0}
              onClick={() => void submit()}
            >
              {t('join.submit')}
            </Button>
          </div>
        </Card>

        <div style={{ marginTop: 'var(--s-4)' }}>
          <Disclosure summary={t('join.knowBefore')} icon="shield">
            <TemporaryNotice hours={hours} />
            <NotAnonymousNotice />
            <EndpointCopyingNotice />
          </Disclosure>
        </div>
      </div>
    </ThemedRegion>
  );
}
